import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/help/")({
	component: HelpHub,
});

const categoryCards = [
	{
		icon: "rocket_launch",
		title: "Getting Started",
		to: "/help/quick-start" as const,
		links: [
			{ label: "Quickstart", to: "/help/quick-start" as const },
			{
				label: "What is AgentFramer?",
				to: "/help/what-is-agentframer" as const,
			},
			{ label: "First MCP Connection", to: "/help/mcp" as const },
		],
	},
	{
		icon: "terminal",
		title: "MCP & API Docs",
		to: "/help/mcp-tools" as const,
		links: [
			{ label: "MCP Overview", to: "/help/mcp" as const },
			{ label: "MCP Tools Reference", to: "/help/mcp-tools" as const },
			{ label: "Tool Call Patterns", to: "/help/tool-call-patterns" as const },
		],
	},
	{
		icon: "group",
		title: "Team & Workspaces",
		to: "/help/inviting-members" as const,
		links: [
			{ label: "Inviting Members", to: "/help/inviting-members" as const },
			{ label: "Roles and Permissions", to: "/help/roles" as const },
			{ label: "Workspace Access", to: "/help/workspace-access" as const },
		],
	},
];

const faqs = [
	{
		q: "Which AI clients work with AgentFramer?",
		a: "Any MCP-compatible client works, Claude Desktop, Cursor, Windsurf, and others. If your assistant supports MCP tool calls, it can use AgentFramer to generate images, video, and audio.",
	},
	{
		q: "Do I need to set up my own AI provider API keys?",
		a: "No. AgentFramer pre-configures all provider credentials. Top up your balance and your agent can start generating immediately, no provider accounts, no API key rotation.",
	},
	{
		q: "What types of media can my agent generate?",
		a: "Your agent can generate images from text prompts, video from text or image input, and audio including music, voice, and sound effects. All outputs are stored and returned as URLs your agent can pass downstream.",
	},
	{
		q: "How does my agent retrieve a generated file?",
		a: "Generation jobs are async. Your agent calls a tool, gets a job ID back, and polls for completion. Once done, it retrieves the output URL, a direct CDN link ready to store or pass downstream. The full pattern is in the Tool Call Patterns guide.",
	},
];

const docsSearchIndex = [
	{
		title: "Quickstart",
		description:
			"Set up the MCP bridge, connect your client, and run your first generation.",
		href: "/help/quick-start" as const,
		keywords: [
			"quickstart",
			"setup",
			"connect",
			"first generation",
			"mcp bridge",
			"install",
		],
		type: "Guide",
	},
	{
		title: "What is AgentFramer?",
		description:
			"Overview of the product, architecture, and how the MCP layer fits in.",
		href: "/help/what-is-agentframer" as const,
		keywords: [
			"overview",
			"architecture",
			"how it works",
			"agentframer",
			"concepts",
		],
		type: "Overview",
	},
	{
		title: "Inviting Members",
		description: "Add teammates to your workspace and manage invitations.",
		href: "/help/inviting-members" as const,
		keywords: ["team", "invite", "workspace", "members", "collaboration"],
		type: "Guide",
	},
	{
		title: "Roles and Permissions",
		description: "Understand workspace roles and who can do what.",
		href: "/help/roles" as const,
		keywords: ["roles", "permissions", "workspace access", "team"],
		type: "Reference",
	},
	{
		title: "MCP Overview",
		description:
			"What MCP is, how AgentFramer implements it, and how to connect your client.",
		href: "/help/mcp" as const,
		keywords: ["mcp", "overview", "protocol", "connect", "client", "assistant"],
		type: "Guide",
	},
	{
		title: "MCP Tools Reference",
		description:
			"Live reference for the MCP tools available today, including media and workspace tools.",
		href: "/help/mcp-tools" as const,
		keywords: [
			"mcp tools",
			"reference",
			"generate_image",
			"generate_video",
			"generate_audio",
			"workspace tools",
			"api",
		],
		type: "Reference",
	},
	{
		title: "Tool Call Patterns",
		description:
			"Recommended request shapes for one-shot generation, model overrides, and generation recovery.",
		href: "/help/tool-call-patterns" as const,
		keywords: [
			"tool call patterns",
			"mcp patterns",
			"agent workflow",
			"recovery",
			"model override",
		],
		type: "Guide",
	},
	{
		title: "Workspace Access",
		description:
			"How active workspace context affects MCP access, billing, and team actions.",
		href: "/help/workspace-access" as const,
		keywords: [
			"workspace access",
			"active workspace",
			"switch workspace",
			"team access",
			"permissions",
		],
		type: "Guide",
	},
] as const;

export function HelpHubContent() {
	const [openFaq, setOpenFaq] = useState<string | null>(null);
	const [focused, setFocused] = useState(false);
	const [query, setQuery] = useState("");
	const normalizedQuery = query.trim().toLowerCase();
	const searchResults = normalizedQuery
		? [
				...docsSearchIndex
					.filter((item) =>
						[item.title, item.description, item.type, ...item.keywords]
							.join(" ")
							.toLowerCase()
							.includes(normalizedQuery),
					)
					.map((item) => ({ kind: "doc" as const, ...item })),
				...faqs
					.filter((faq) =>
						`${faq.q} ${faq.a}`.toLowerCase().includes(normalizedQuery),
					)
					.map((faq) => ({ kind: "faq" as const, ...faq })),
			]
		: [];

	return (
		<>
			{/* Hero Search */}
			<section className="pb-16 px-8 lg:px-12 w-full">
				<div className="relative mb-12">
					<p
						className="text-[11px] font-bold uppercase tracking-[0.1em] mb-4"
						style={{ fontFamily: "inherit", color: "#6B3FF2" }}
					>
						Help Center
					</p>
					<h2
						className="text-5xl md:text-7xl font-semibold tracking-tight mb-8 leading-[0.9] text-left"
						style={{
							fontFamily: "'Geist', -apple-system, sans-serif",
							fontWeight: 600,
							color: "var(--km-on-surface)",
						}}
					>
						Documentation
					</h2>
					<div className="relative max-w-2xl">
						<span
							aria-hidden="true"
							className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							search
						</span>
						<input
							aria-label="Search documentation"
							className="w-full border-none focus:outline-none py-5 pl-16 pr-6 text-lg transition-all"
							style={{
								backgroundColor: "var(--km-surface-high)",
								color: "var(--km-on-surface)",
								fontFamily: "inherit",
								border: focused
									? "1px solid #6B3FF2"
									: "1px solid rgba(255,255,255,0.08)",
							}}
							placeholder="Search documentation, MCP tools, or error codes..."
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onFocus={() => setFocused(true)}
							onBlur={() => setFocused(false)}
						/>
					</div>
				</div>

				{normalizedQuery ? (
					<div
						className="p-6 mb-12"
						style={{
							background: "var(--km-surface-high)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<div className="flex items-center justify-between gap-4 mb-6">
							<div>
								<h3
									className="text-xl font-semibold tracking-tight"
									style={{
										color: "var(--km-on-surface)",
										fontFamily: "'Geist', -apple-system, sans-serif",
									}}
								>
									Search Results
								</h3>
								<p
									className="text-sm"
									style={{ color: "var(--km-on-surface-muted)" }}
								>
									{searchResults.length} matches for &ldquo;{query.trim()}
									&rdquo;
								</p>
							</div>
							<button
								type="button"
								onClick={() => setQuery("")}
								className="text-xs font-bold uppercase tracking-wide transition-colors"
								style={{
									fontFamily: "inherit",
									color: "var(--km-on-surface-muted)",
								}}
							>
								Clear
							</button>
						</div>
						{searchResults.length > 0 ? (
							<div className="space-y-3">
								{searchResults.map((result) =>
									result.kind === "doc" ? (
										<Link
											key={result.href}
											to={result.href}
											className="block p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
											style={{
												background: "var(--km-surface)",
												border: "1px solid rgba(255,255,255,0.08)",
												borderLeft: "3px solid rgba(255,255,255,0.08)",
											}}
											onMouseEnter={(e) => {
												(e.currentTarget as HTMLElement).style.borderLeftColor =
													"#6B3FF2";
											}}
											onMouseLeave={(e) => {
												(e.currentTarget as HTMLElement).style.borderLeftColor =
													"rgba(255,255,255,0.08)";
											}}
											onFocus={(e) => {
												(e.currentTarget as HTMLElement).style.borderLeftColor =
													"#6B3FF2";
											}}
											onBlur={(e) => {
												(e.currentTarget as HTMLElement).style.borderLeftColor =
													"rgba(255,255,255,0.08)";
											}}
										>
											<div className="flex items-center justify-between gap-4 mb-1">
												<h4
													className="text-base font-semibold"
													style={{
														color: "var(--km-on-surface)",
														fontFamily: "'Geist', -apple-system, sans-serif",
													}}
												>
													{result.title}
												</h4>
												<span
													className="text-[10px] uppercase tracking-[0.1em] font-bold"
													style={{
														fontFamily: "inherit",
														color: "#6B3FF2",
													}}
												>
													{result.type}
												</span>
											</div>
											<p
												className="text-sm"
												style={{ color: "var(--km-on-surface-muted)" }}
											>
												{result.description}
											</p>
										</Link>
									) : (
										<div
											key={result.q}
											className="p-4"
											style={{
												background: "var(--km-surface)",
												border: "1px solid rgba(255,255,255,0.08)",
											}}
										>
											<div
												className="mb-1 text-[10px] uppercase tracking-[0.1em] font-bold"
												style={{
													fontFamily: "inherit",
													color: "#6B3FF2",
												}}
											>
												FAQ
											</div>
											<h4
												className="text-base font-semibold mb-2"
												style={{
													color: "var(--km-on-surface)",
													fontFamily: "'Geist', -apple-system, sans-serif",
												}}
											>
												{result.q}
											</h4>
											<p
												className="text-sm"
												style={{ color: "var(--km-on-surface-muted)" }}
											>
												{result.a}
											</p>
										</div>
									),
								)}
							</div>
						) : (
							<p
								className="text-sm"
								style={{ color: "var(--km-on-surface-muted)" }}
							>
								No docs matched your search. Try terms like `mcp`, `workspace`,
								`roles`, or `quickstart`.
							</p>
						)}
					</div>
				) : null}

				{/* Category Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-1">
					{categoryCards.map((card) => (
						<Link
							key={card.title}
							to={card.to}
							className="p-8 transition-all group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
							style={{
								background: "var(--km-surface-high)",
								border: "1px solid rgba(255,255,255,0.08)",
								borderTop: "3px solid #6B3FF2",
							}}
						>
							<div className="mb-6 flex justify-between items-start">
								<span
									aria-hidden="true"
									className="material-symbols-outlined text-3xl"
									style={{ color: "#6B3FF2" }}
								>
									{card.icon}
								</span>
								<span
									aria-hidden="true"
									className="material-symbols-outlined transition-colors group-hover:text-[#6B3FF2]"
									style={{ color: "var(--km-on-surface-muted)" }}
								>
									arrow_outward
								</span>
							</div>
							<h3
								className="text-[11px] font-bold uppercase tracking-[0.1em] mb-4"
								style={{
									fontFamily: "inherit",
									color: "#6B3FF2",
								}}
							>
								{card.title}
							</h3>
							<ul
								className="space-y-3 text-sm"
								style={{
									fontFamily: "'Geist', -apple-system, sans-serif",
									color: "var(--km-on-surface-muted)",
								}}
							>
								{card.links.map((link) => (
									<li key={link.label}>
										<Link
											to={link.to}
											className="flex items-center gap-2 transition-colors hover:text-[var(--km-on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
											onClick={(e) => e.stopPropagation()}
										>
											<span
												className="w-1 h-1 flex-shrink-0"
												style={{ backgroundColor: "#6B3FF2" }}
											/>
											{link.label}
										</Link>
									</li>
								))}
							</ul>
						</Link>
					))}
				</div>
			</section>

			{/* FAQ & Community */}
			<section className="px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24">
				{/* FAQ */}
				<div className="lg:col-span-8">
					<div className="mb-10">
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-3"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							FAQ
						</p>
						<h2
							className="text-3xl font-semibold tracking-tight mb-2"
							style={{
								color: "var(--km-on-surface)",
								fontFamily: "'Geist', -apple-system, sans-serif",
							}}
						>
							Frequently Asked Questions
						</h2>
						<p
							className="text-sm"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							Common questions about AgentFramer and MCP media generation.
						</p>
					</div>
					<div className="space-y-4">
						{faqs.map((faq) => {
							const isOpen = openFaq === faq.q;
							return (
								<button
									type="button"
									key={faq.q}
									aria-expanded={isOpen}
									className="py-6 cursor-pointer w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
									onClick={() => setOpenFaq(isOpen ? null : faq.q)}
								>
									<div className="flex justify-between items-center">
										<span
											className="text-base font-bold transition-colors"
											style={{
												color: isOpen ? "#6B3FF2" : "var(--km-on-surface)",
												fontFamily: "'Geist', -apple-system, sans-serif",
											}}
										>
											{faq.q}
										</span>
										<span
											aria-hidden="true"
											className="material-symbols-outlined shrink-0"
											style={{ color: "var(--km-on-surface-muted)" }}
										>
											{isOpen ? "remove" : "add"}
										</span>
									</div>
									{isOpen && faq.a && (
										<p
											className="mt-4 text-sm leading-relaxed max-w-2xl"
											style={{ color: "var(--km-on-surface-muted)" }}
										>
											{faq.a}
										</p>
									)}
								</button>
							);
						})}
					</div>
				</div>

				{/* Community & Contact Sidebar */}
				<div className="lg:col-span-4 space-y-1">
					{/* Help Channels */}
					<div
						className="p-6 relative overflow-hidden"
						style={{
							background: "var(--km-surface-high)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<h4
							className="text-[10px] font-bold uppercase tracking-[0.1em] mb-3 relative z-10"
							style={{
								fontFamily: "inherit",
								color: "#6B3FF2",
							}}
						>
							Support
						</h4>
						<p
							className="text-sm mb-6 relative z-10"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							Use the docs first. If you're stuck, send feedback from the
							dashboard with the exact prompt, model, and error.
						</p>
						<Link
							to="/dashboard/settings"
							className="block w-full py-4 font-bold text-sm tracking-tight text-center relative z-10 transition-transform active:scale-95"
							style={{ backgroundColor: "#6B3FF2", color: "#ffffff" }}
						>
							Open dashboard
						</Link>
					</div>

					{/* Status Indicator */}
					<div
						className="p-4 flex items-center justify-between"
						style={{
							background: "var(--km-surface-high)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #2b9a66",
						}}
					>
						<div className="flex items-center gap-2">
							<span
								className="w-2 h-2 rounded-full animate-pulse"
								style={{ backgroundColor: "#2b9a66" }}
							/>
							<span
								className="text-[10px] uppercase tracking-wide font-bold"
								style={{
									fontFamily: "inherit",
									color: "#2b9a66",
								}}
							>
								System Status
							</span>
						</div>
						<span
							className="text-[10px] uppercase tracking-wide font-bold"
							style={{
								fontFamily: "inherit",
								color: "#2b9a66",
							}}
						>
							All Operational
						</span>
					</div>
				</div>
			</section>

			{/* Docs CTA */}
			<section className="px-8 lg:px-12 w-full pb-20">
				<div
					className="flex flex-col sm:flex-row gap-6 items-start sm:items-center p-8"
					style={{
						background: "var(--km-surface-high)",
						border: "1px solid rgba(255,255,255,0.08)",
						borderLeft: "3px solid #6B3FF2",
					}}
				>
					<div className="flex-1">
						<p
							className="text-[10px] font-bold uppercase tracking-[0.1em] mb-1"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Next Steps
						</p>
						<p
							className="text-base font-semibold mb-1"
							style={{
								color: "var(--km-on-surface)",
								fontFamily: "'Geist', -apple-system, sans-serif",
							}}
						>
							Looking for detailed guides?
						</p>
						<p
							className="text-sm"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							Browse the step-by-step documentation for MCP setup, API
							reference, and more.
						</p>
					</div>
					<div className="flex gap-3 shrink-0">
						<Link
							to="/help/quick-start"
							className="px-5 py-2.5 text-sm font-bold transition-transform active:scale-95"
							style={{ backgroundColor: "#6B3FF2", color: "#ffffff" }}
						>
							Quick Start Guide
						</Link>
						<Link
							to="/pricing"
							className="px-5 py-2.5 text-sm font-bold transition-colors"
							style={{
								border: "1px solid rgba(255,255,255,0.08)",
								color: "var(--km-on-surface-muted)",
							}}
						>
							Pricing
						</Link>
					</div>
				</div>
			</section>
		</>
	);
}

function HelpHub() {
	return <HelpHubContent />;
}
